'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/** Coordenada a partir do canto inferior direito da viewport, em px. */
export interface DragPos {
  bottom: number;
  right: number;
}

/** Medidas de QUEM esta sendo arrastado — e o que define o limite da tela. */
export interface DragSize {
  width: number;
  height: number;
}

/**
 * Threshold que separa clique de arrasto. MESMO valor do card de pausa
 * (`components/layout/break-banner.tsx`), que e a referencia de arrasto do
 * produto — dois thresholds diferentes fariam o mesmo gesto virar clique num
 * canto da tela e arrasto no outro.
 */
export const DRAG_THRESHOLD_PX = 5;

/**
 * Controles que, DENTRO de uma alca, nunca iniciam arrasto.
 *
 * O painel do webphone tem teclado, abas e botoes de mudo/hold/transferir; o
 * cartao da chamada tem Atender e Recusar. Deixar o `pointerdown` deles virar
 * arrasto e o caminho conhecido pra "o clique do filho some" — ja aconteceu
 * neste produto com `setPointerCapture` (por isso ele NAO e usado aqui; ver o
 * comentario do `onPointerDown`).
 *
 * A comparacao com `currentTarget` no `onPointerDown` e o que permite a bolha
 * ser ela mesma um `<button>` arrastavel: o filtro so vale pra controle que
 * esta DENTRO da alca, nunca pra propria alca.
 */
const INTERACTIVE_SELECTOR =
  'button,a,input,select,textarea,[role="button"],[role="combobox"],[role="tab"]';

/**
 * Prende a coordenada dentro da area visivel, dado o tamanho de QUEM vai ser
 * desenhado ali.
 *
 * O tamanho e parametro, e nao uma constante fixa, por causa da #441: a bolha,
 * o painel e o cartao da chamada entrante compartilham a MESMA coordenada, mas
 * nao o mesmo limite. Quem chama diz de quem e o limite — sem default, pra
 * ninguem herdar as medidas da bolha por descuido.
 */
export function clampToViewport(
  p: DragPos,
  width: number,
  height: number,
): DragPos {
  if (typeof window === 'undefined') return p;
  const maxRight = Math.max(0, window.innerWidth - width);
  const maxBottom = Math.max(0, window.innerHeight - height);
  return {
    bottom: Math.min(Math.max(0, p.bottom), maxBottom),
    right: Math.min(Math.max(0, p.right), maxRight),
  };
}

interface UseDragPositionOptions {
  /** Posicao ATUAL renderizada (ja presa a viewport) — origem do proximo arrasto. */
  pos: DragPos;
  /** Medidas de quem esta sendo arrastado AGORA (o cartao minimizado mede menos). */
  size: DragSize;
  /** A cada movimento, com a posicao ja presa a viewport. */
  onChange: (next: DragPos) => void;
  /**
   * Ao SOLTAR, uma vez so. E aqui que mora a persistencia de quem persiste —
   * gravar no `localStorage` a cada `pointermove` seriam dezenas de escritas
   * sincronas por segundo, e quem nao persiste simplesmente nao passa este
   * callback.
   */
  onCommit?: (final: DragPos) => void;
}

/**
 * Arrasto por ponteiro (mouse + toque) — UMA mecanica pro webphone inteiro.
 *
 * Copia deliberada do padrao do `break-banner.tsx` (pausa): Pointer Events,
 * listeners no `document`, threshold de 5px e limite de viewport aplicado a
 * cada movimento. Diferenca consciente: la a ancora e `top/left` (o pill nasce
 * centralizado por CSS); aqui e `bottom/right`, que e como a bolha, o painel e
 * o cartao sempre foram posicionados.
 *
 * ⚠️ O ciclo de vida dos listeners NAO depende de identidade de funcao — e o
 * defeito que este hook nasceu corrigindo. A versao anterior vivia inline na
 * `WebphoneBubble`: os handlers dependiam de `onPosChange`, uma arrow inline do
 * `WebphoneSticky` que mudava a cada render, e o efeito de limpeza tinha esses
 * handlers nas deps. Bastava o primeiro `pointermove` chamar `setPos`, o pai
 * re-renderizar, as identidades mudarem — e a limpeza arrancava os listeners no
 * meio do arrasto. Na pratica a bolha andava o delta do primeiro movimento (uns
 * poucos px) e travava: "arrastavel" so no papel. Reproduzido em teste antes de
 * mexer (`use-drag-position.test.tsx` e `webphone-sticky.test.tsx`).
 *
 * Aqui os handlers sao estaveis (deps vazias, tudo lido de ref) E o desligamento
 * e por `AbortController`, que nem precisa reencontrar a funcao registrada.
 */
export function useDragPosition({
  pos,
  size,
  onChange,
  onCommit,
}: UseDragPositionOptions) {
  const [dragging, setDragging] = useState(false);

  // Tudo que muda a cada render entra por ref: os handlers precisam ser
  // estaveis (ver o aviso no docblock) e ainda assim enxergar o valor de agora.
  const sizeRef = useRef(size);
  const onChangeRef = useRef(onChange);
  const onCommitRef = useRef(onCommit);

  // Sincronizado em efeito, e nao no corpo do render (mexer em ref durante o
  // render e o que a regra `react-hooks/refs` proibe). Sem deps de proposito:
  // roda depois de TODO commit, e todo gesto de ponteiro comeca depois de um
  // commit — os handlers nunca leem um valor de antes.
  useEffect(() => {
    sizeRef.current = size;
    onChangeRef.current = onChange;
    onCommitRef.current = onCommit;
  });

  const draggingRef = useRef<{
    startX: number;
    startY: number;
    startPos: DragPos;
    lastPos: DragPos;
    pointerId: number;
  } | null>(null);
  const movedRef = useRef(false);
  // Os listeners do gesto sao registrados com `signal` e desligados por
  // `abort()`. Alem de ser uma linha so, isso resolve na raiz o problema que
  // derrubava o arrasto antigo: nao existe mais "remover o listener certo",
  // entao nenhuma identidade de funcao precisa se manter estavel pra limpeza
  // funcionar.
  const gestoRef = useRef<AbortController | null>(null);

  const onPointerMove = useCallback((e: PointerEvent) => {
    const d = draggingRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (
      !movedRef.current &&
      (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)
    ) {
      movedRef.current = true;
    }
    // bottom/right crescem pra ESQUERDA/CIMA, dai inverter o sinal do dx/dy.
    const next = clampToViewport(
      { bottom: d.startPos.bottom - dy, right: d.startPos.right - dx },
      sizeRef.current.width,
      sizeRef.current.height,
    );
    d.lastPos = next;
    onChangeRef.current(next);
  }, []);

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      const d = draggingRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      draggingRef.current = null;
      setDragging(false);
      gestoRef.current?.abort();
      gestoRef.current = null;
      // Só persiste o que de fato virou arrasto: um clique simples na alça não
      // pode reescrever a posição guardada.
      if (movedRef.current) onCommitRef.current?.(d.lastPos);
    },
    [],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return; // só botão esquerdo / toque primário
    const alvo = e.target as HTMLElement | null;
    const controle = alvo?.closest(INTERACTIVE_SELECTOR) ?? null;
    // Controle DENTRO da alça (Atender, Minimizar, tecla do teclado...): o
    // gesto é dele, não do arrasto. A própria alça sendo um controle — a bolha
    // é um <button> — continua arrastável.
    if (controle && controle !== e.currentTarget) return;
    // Sem `setPointerCapture`: os listeners no `document` já cobrem o ponteiro
    // sair do elemento durante o arrasto, e a captura é a causa conhecida de
    // clique de filho engolido neste produto.
    movedRef.current = false;
    draggingRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPos: pos,
      lastPos: pos,
      pointerId: e.pointerId,
    };
    setDragging(true);
    // Listeners no `document`, e nao no elemento: o ponteiro sai da alca o
    // tempo todo durante um arrasto.
    gestoRef.current?.abort();
    const gesto = new AbortController();
    gestoRef.current = gesto;
    const opts = { signal: gesto.signal };
    document.addEventListener('pointermove', onPointerMove, opts);
    document.addEventListener('pointerup', onPointerUp, opts);
    document.addEventListener('pointercancel', onPointerUp, opts);
  };

  // Desmontar no meio do gesto (a chamada acabou, o painel fechou) nao pode
  // deixar listener vivo. Deps vazias: e limpeza de desmonte, nao de render.
  useEffect(() => {
    return () => gestoRef.current?.abort();
  }, []);

  return {
    /** Vai na ALÇA (a região por onde se arrasta), não no elemento inteiro. */
    onPointerDown,
    /** true enquanto o ponteiro está arrastando — pra desligar transição de CSS. */
    dragging,
    /**
     * Houve arrasto acima do threshold desde o último `pointerdown`? Consome a
     * marca: quem arrasta um elemento clicável (a bolha) usa isto pra ignorar o
     * `click` que o navegador dispara ao soltar.
     */
    wasDragged: () => {
      const moved = movedRef.current;
      movedRef.current = false;
      return moved;
    },
  };
}
